import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  ShieldCheck,
  Activity,
  GraduationCap,
  Trophy,
  Instagram,
  Linkedin,
  Clock,
} from "lucide-react";
import WhatsAppButton from "../components/site/WhatsAppButton";
import LeadForm from "../components/site/LeadForm";
import Seo from "../lib/seo";
import api from "../lib/api";
import { CLINIC } from "../data/content";

const DR = {
  name: "Dr. Amit Sharma",
  role: "Clinic Head · Head Physiotherapist",
  credentials:
    "MPT · Fellowship in Sports Rehabilitation (Level II) · 17+ years of clinical experience",
  photo: "/team/amit-sharma.jpg",
  linkedIn: "https://www.linkedin.com/",
  instagram: "https://www.instagram.com/",
};

const EXPERTISE = [
  {
    Icon: Trophy,
    tag: "Sports",
    title: "Sports Rehabilitation",
    body:
      "Recreational to competitive athletes — assessment, progressive loading, sport-specific conditioning and structured return-to-play testing.",
  },
  {
    Icon: Activity,
    tag: "Knee",
    title: "ACL & Complex Knee Rehab",
    body:
      "Pre-operative preparation, post-operative rehabilitation and objective return-to-sport milestones for ACL, PCL and multi-ligament injuries.",
  },
  {
    Icon: ShieldCheck,
    tag: "Surgery",
    title: "Post-Surgical Recovery",
    body:
      "Structured, phase-based recovery after orthopaedic procedures — with clear expectations, timelines and objective progress markers.",
  },
  {
    Icon: GraduationCap,
    tag: "Leadership",
    title: "Clinical Leadership & Mentoring",
    body:
      "Leads the Physio Hyve team, oversees quality of care, standard of assessment and continuing education across the clinic.",
  },
];

const HIGHLIGHTS = [
  "17+ years of clinical experience across sports, orthopaedic and post-surgical rehabilitation.",
  "Fellowship-trained in Sports Rehabilitation (Level II).",
  "Leads the clinic's clinical standards, protocols and team mentoring.",
  "Regular contributor to the Physio Hyve journal — writing evidence-informed articles for patients.",
  "Committed to honest, outcome-focused communication with patients and their families.",
];

const personLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: DR.name,
  jobTitle: "Head Physiotherapist, Clinic Head — Physio Hyve Clinic",
  description:
    "Head Physiotherapist and Clinic Head at Physio Hyve Clinic, Nangloi, Delhi. 17+ years of experience in sports rehabilitation, ACL & post-surgical recovery, with a Fellowship in Sports Rehabilitation (Level II).",
  image: DR.photo,
  worksFor: {
    "@type": "MedicalClinic",
    name: "Physio Hyve Clinic",
    url: typeof window !== "undefined" ? window.location.origin : "",
  },
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Fellowship",
      name: "Fellowship in Sports Rehabilitation (Level II)",
    },
  ],
  knowsAbout: [
    "Sports Rehabilitation",
    "ACL Rehabilitation",
    "Post-Surgical Recovery",
    "Return-to-Sport Testing",
    "Orthopaedic Physiotherapy",
    "Clinical Leadership",
  ],
  sameAs: [DR.linkedIn],
};

export default function AmitSharmaProfile() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const waMsg = "Hello Physio Hyve, I'd like to enquire about an assessment with Dr. Amit Sharma.";

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => {
        const mine = (r.data || []).filter(
          (p) => (p.author || "").toLowerCase().includes("amit sharma")
        );
        setPosts(mine);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Seo
        title="Dr. Amit Sharma — Head Physiotherapist, Sports Rehab Specialist | Physio Hyve"
        description="Dr. Amit Sharma is Clinic Head and Head Physiotherapist at Physio Hyve Clinic, Nangloi, Delhi. 17+ years' experience, Fellowship in Sports Rehabilitation (Level II). Sports, ACL and post-surgical rehabilitation."
        path="/team/amit-sharma"
        image={DR.photo}
        jsonLd={personLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-cream/60 via-brand-bg to-brand-bg" />
        <div className="container-wide pt-16 pb-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3 flex items-center gap-2">
              <Award size={14} /> Clinic Head · Head Physiotherapist
            </div>
            <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">
              Dr. Amit Sharma — <span className="text-brand-terracotta">17 years</span> of rehabilitation, one honest approach.
            </h1>
            <p className="text-brand-ink2 mt-6 text-lg max-w-2xl">
              Head of Physio Hyve Clinic. Fellowship-trained in Sports Rehabilitation (Level II).
              Dr. Amit Sharma leads assessment, complex-case management and return-to-sport
              programmes — and mentors the wider clinical team.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#amit-enquiry"
                data-testid="amit-enquire-cta"
                className="btn-primary"
              >
                Enquire About Assessment <ArrowRight size={16} />
              </a>
              <WhatsAppButton message={waMsg} data-testid="amit-whatsapp-cta">
                Ask on WhatsApp
              </WhatsAppButton>
            </div>

            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              {[
                "17+ yrs experience",
                "Fellowship — Sports Rehab (L-II)",
                "ACL & post-surgical rehab",
                "Return-to-sport testing",
              ].map((chip) => (
                <span
                  key={chip}
                  className="bg-brand-cream text-brand-olive rounded-full px-3 py-1.5"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-soft overflow-hidden" data-testid="amit-doctor-card">
              <div className="aspect-[4/5] bg-brand-cream overflow-hidden">
                <img
                  src={DR.photo}
                  alt={`${DR.name} — Head Physiotherapist and Clinic Head at Physio Hyve Clinic`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="font-heading text-2xl text-brand-ink">{DR.name}</div>
                <div className="text-sm text-brand-terracotta mt-1">{DR.role}</div>
                <p className="text-sm text-brand-ink2 mt-3">{DR.credentials}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-brand-ink2">
                  <Award size={14} className="text-brand-olive" />
                  <span>Sports · ACL · Post-Surgical · Return-to-Sport</span>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <a
                    href={DR.linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-olive hover:underline"
                    data-testid="amit-linkedin-link"
                  >
                    <Linkedin size={14} /> LinkedIn
                  </a>
                  <a
                    href={DR.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-olive hover:underline"
                    data-testid="amit-instagram-link"
                  >
                    <Instagram size={14} /> Follow
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="container-wide py-14">
        <div className="max-w-2xl">
          <div className="eyebrow mb-3">Areas of expertise</div>
          <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
            Complex rehabilitation, delivered with clarity.
          </h2>
          <p className="text-brand-ink2 mt-4">
            Dr. Amit&apos;s clinical focus is sports and post-surgical rehabilitation — with a
            particular interest in ACL and complex knee cases. Every plan begins with an individual
            assessment and honest conversation about expected outcomes.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {EXPERTISE.map(({ Icon, tag, title, body }) => (
            <div
              key={title}
              className="card-soft p-6 hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow"
              data-testid={`amit-expertise-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <div className="w-10 h-10 rounded-xl bg-brand-cream text-brand-olive flex items-center justify-center mb-3">
                <Icon size={18} />
              </div>
              <div className="text-xs uppercase tracking-widest text-brand-terracotta">{tag}</div>
              <h3 className="font-heading text-xl text-brand-ink mt-1">{title}</h3>
              <p className="text-sm text-brand-ink2 mt-2">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Highlights + enquiry */}
      <section className="bg-brand-cream/40 py-14">
        <div className="container-wide grid md:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow mb-3">About Dr. Amit</div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              A clinician who listens first — then plans.
            </h2>
            <ul className="mt-6 space-y-3 text-brand-ink2">
              {HIGHLIGHTS.map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-terracotta shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-soft p-6" id="amit-enquiry">
            <div className="font-heading text-xl text-brand-ink">
              Enquire about a session with Dr. Amit
            </div>
            <p className="text-sm text-brand-ink2 mt-2">
              Share what&apos;s troubling you and a preferred time. Our team will get back to guide
              you on the right assessment pathway.
            </p>
            <div className="mt-5">
              <LeadForm
                category="sports-rehab"
                sourcePage="/team/amit-sharma"
                defaultConcern="Enquiry for assessment with Dr. Amit Sharma"
                compact
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog authored by Dr. Amit */}
      <section className="container-wide py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2">
              <BookOpen size={14} /> Written by Dr. Amit
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Latest articles from the clinic head.
            </h2>
            <p className="text-brand-ink2 mt-3 max-w-2xl">
              Regular, evidence-informed writing on rehabilitation, sports injuries and recovery —
              published on the Physio Hyve journal.
            </p>
          </div>
          <Link to="/blog" className="btn-outline" data-testid="amit-view-blog">
            View all articles <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="mt-10 text-brand-ink2">Loading articles…</div>
        ) : posts.length === 0 ? (
          <div className="mt-10 card-soft p-8 text-brand-ink2">
            No articles published yet — Dr. Amit&apos;s upcoming pieces will appear here.
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {posts.slice(0, 6).map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="card-soft overflow-hidden group"
                data-testid={`amit-blog-${p.slug}`}
              >
                {p.featured_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-brand-cream">
                    <img
                      src={p.featured_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-brand-ink2">
                    <span className="uppercase tracking-widest text-brand-terracotta">
                      {p.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {p.reading_time || 3} min
                    </span>
                  </div>
                  <h3 className="font-heading text-xl text-brand-ink mt-3 group-hover:text-brand-olive">
                    {p.title}
                  </h3>
                  <p className="text-sm text-brand-ink2 mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 text-sm text-brand-olive inline-flex items-center gap-1">
                    Read <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Final CTA */}
      <section className="container-wide pb-24">
        <div className="rounded-[24px] bg-brand-blue-dark text-brand-cream p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="eyebrow text-brand-amber">Speak with Dr. Amit&apos;s team</div>
            <h3 className="font-heading text-2xl md:text-3xl mt-2">
              Have a sports injury, ACL question or post-surgical concern?
            </h3>
            <p className="text-brand-cream/80 mt-2 max-w-xl text-sm">
              Send us a short enquiry — we&apos;ll get back with the next step for an in-clinic
              assessment or a WhatsApp conversation.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#amit-enquiry" className="btn-primary" data-testid="amit-final-enquire">
              Send an Enquiry
            </a>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="btn-outline"
              data-testid="amit-final-call"
            >
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
